import { BicepsFlexed, Dumbbell } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Account } from "../lib/api.ts";
import { avatarUrl } from "../lib/api.ts";
import { ROLE_LABEL, displayName, initials } from "../lib/profile.ts";

const ROLE_ICON: Record<Account["role"], LucideIcon> = { athlete: Dumbbell, coach: BicepsFlexed };

/**
 * The picture, or the initials until there is one, with the role in its
 * corner: at a glance, whose screen this is and whether they train or coach.
 */
export function Avatar({
  account,
  size,
  mark: withMark = true,
}: {
  account: Account;
  size: number;
  /** The role in the corner; too small to read below 32 px. */
  mark?: boolean;
}) {
  const url = avatarUrl(account);
  const Icon = ROLE_ICON[account.role];
  const mark = Math.round(size * 0.36);
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      {url ? (
        <img
          src={url}
          alt={displayName(account)}
          className="block h-full w-full rounded-full border border-line bg-surface-high object-cover"
        />
      ) : (
        <span
          role="img"
          aria-label={displayName(account)}
          className="display flex h-full w-full items-center justify-center rounded-full border border-line bg-base font-bold text-ink-soft"
          style={{ fontSize: size * 0.38 }}
        >
          {initials(account)}
        </span>
      )}
      {withMark && (
        <span
          title={ROLE_LABEL[account.role]}
          className={[
            "absolute -right-0.5 -bottom-0.5 flex items-center justify-center rounded-full ring-2 ring-base",
            account.role === "coach" ? "bg-accent text-accent-ink" : "bg-ink text-[var(--color-base)]",
          ].join(" ")}
          style={{ width: mark, height: mark }}
        >
          <Icon size={Math.round(mark * 0.58)} aria-hidden />
        </span>
      )}
    </span>
  );
}

/** The role in words, beside the name. */
export function RoleBadge({ role }: { role: Account["role"] }) {
  const Icon = ROLE_ICON[role];
  return (
    <span
      className={[
        "inline-flex h-6 items-center gap-1 rounded-full px-2 text-[0.75rem] font-semibold",
        role === "coach" ? "bg-accent text-accent-ink" : "bg-surface-high text-ink-soft",
      ].join(" ")}
    >
      <Icon size={13} aria-hidden />
      {ROLE_LABEL[role]}
    </span>
  );
}

/** A session where the coach logged sets: his colour and icon, the same as on his account. */
export function CoachedBadge({ className = "" }: { className?: string }) {
  return (
    <span
      className={[
        "inline-flex h-5 shrink-0 items-center gap-1 rounded-full bg-accent px-1.5 text-[0.7rem] font-semibold text-accent-ink",
        className,
      ].join(" ")}
    >
      <BicepsFlexed size={11} aria-hidden />
      Avec coach
    </span>
  );
}
